import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import {
    TrendingUp,
    Activity,
    BarChart3,
    PieChart,
    CircleDollarSign,
    Calendar
} from 'lucide-react';
import { useDashboardData } from 'hooks/useDashboardData';
import { DashboardFilters, TimeFrame } from 'types/dashboard';
import MetricsCards from './MetricsCards';
import TimeSeriesChart from './TimeSeriesChart';
import ModelUsageChart from './ModelUsageChart';
import CostPerModelChart from './CostPerModelChart';
import ToolUsageChart from './ToolUsageChart';

const AIUsageDashboard: React.FC = () => {
    const [filters, setFilters] = useState<DashboardFilters>({
        timeFrame: '24h'
    });

    const { data, loading, error } = useDashboardData(filters);

    const handleTimeFrameChange = (timeFrame: TimeFrame) => {
        setFilters(prev => ({ ...prev, timeFrame }));
    };

    if (error) {
        return (
            <div className="flex items-center justify-center h-64">
                <div className="text-center">
                    <p className="text-red-500 mb-2">Error loading dashboard data</p>
                    <p className="text-sm text-gray-500">{error}</p>
                </div>
            </div>
        );
    }

    return (
        <div className="space-y-6">
            <div
                className="mb-4 px-4 py-2 rounded text-center font-semibold bg-background border border-secondary text-primary"
            >
                <span role="img" aria-label="Demo">🛈</span> This Usage Dashboard is running in <strong>demo mode</strong>. Data is sampled for demonstration purposes.
            </div>
            <div className="flex items-center justify-between">
                <div className="flex items-center mb-4">
                    <BarChart3 className="mr-3 text-primary" size={32} />
                    <div>
                        <h1 className="text-3xl font-bold text-foreground">
                            Usage Dashboard
                        </h1>
                        <p className="text-lg text-muted-foreground">
                            Usage metrics are gathered every 15 minutes.
                        </p>
                    </div>
                </div>

                <div className="flex items-center space-x-2">
                    <Calendar className="h-4 w-4 text-foreground" />
                    <Select value={filters.timeFrame} onValueChange={handleTimeFrameChange}>
                        <SelectTrigger className="w-32">
                            <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem value="1h">Last Hour</SelectItem>
                            <SelectItem value="24h">Last 24 Hours</SelectItem>
                            <SelectItem value="7d">Last 7 Days</SelectItem>
                            <SelectItem value="30d">Last 30 Days</SelectItem>
                        </SelectContent>
                    </Select>
                </div>
            </div>

            {loading ? (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                    {Array.from({ length: 4 }).map((_, i) => (
                        <Card key={i}>
                            <CardHeader className="pb-2">
                                <Skeleton className="h-4 w-24" />
                            </CardHeader>
                            <CardContent>
                                <Skeleton className="h-8 w-16" />
                                <Skeleton className="h-3 w-20 mt-2" />
                            </CardContent>
                        </Card>
                    ))}
                </div>
            ) : (
                <MetricsCards metrics={data?.metrics} />
            )}

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                <Card className="lg:col-span-3">
                    <CardHeader>
                        <CardTitle className="flex items-center">
                            <TrendingUp className="h-5 w-5 mr-2" />
                            Usage Over Time
                        </CardTitle>
                    </CardHeader>
                    <CardContent>
                        {loading ? (
                            <Skeleton className="h-80 w-full" />
                        ) : (
                            <TimeSeriesChart data={data?.timeSeries || []} timeFrame={filters.timeFrame} />
                        )}
                    </CardContent>
                </Card>

                <Card>
                    <CardHeader>
                        <CardTitle className="flex items-center">
                            <PieChart className="h-5 w-5 mr-2" />
                            LLM Model Usage
                        </CardTitle>
                    </CardHeader>
                    <CardContent>
                        {loading ? (
                            <Skeleton className="h-64 w-full" />
                        ) : (
                            <ModelUsageChart data={data?.metrics.llmModels || []} />
                        )}
                    </CardContent>
                </Card>

                <Card>
                    <CardHeader>
                        <CardTitle className="flex items-center">
                            <CircleDollarSign className="h-5 w-5 mr-2" />
                            Cost Per Model
                        </CardTitle>
                    </CardHeader>
                    <CardContent>
                        {loading ? (
                            <Skeleton className="h-64 w-full" />
                        ) : (
                            <CostPerModelChart data={data?.metrics.modelCosts || []} />
                        )}
                    </CardContent>
                </Card>

                <Card>
                    <CardHeader>
                        <CardTitle className="flex items-center">
                            <BarChart3 className="h-5 w-5 mr-2" />
                            Tool Call Usage
                        </CardTitle>
                    </CardHeader>
                    <CardContent>
                        {loading ? (
                            <Skeleton className="h-64 w-full" />
                        ) : (
                            <ToolUsageChart data={data?.metrics.toolCalls || []} />
                        )}
                    </CardContent>
                </Card>
            </div>

            <Card>
                <CardHeader>
                    <CardTitle className="flex items-center">
                        <Activity className="h-5 w-5 mr-2" />
                        LLM Performance Metrics
                    </CardTitle>
                </CardHeader>
                <CardContent>
                    {loading ? (
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                            {Array.from({ length: 3 }).map((_, i) => (
                                <div key={i} className="text-center">
                                    <Skeleton className="h-8 w-16 mx-auto mb-2" />
                                    <Skeleton className="h-4 w-20 mx-auto" />
                                </div>
                            ))}
                        </div>
                    ) : (
                        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                            <div className="text-center">
                                <div className="text-2xl font-bold text-primary">
                                    {data?.metrics.avgTtFT.toFixed(2)}s
                                </div>
                                <div className="text-sm text-foreground">
                                    Average TtFT (Time to First Token)
                                </div>
                            </div>
                            <div className="text-center">
                                <div className="text-2xl font-bold text-primary">
                                    {data?.metrics.avgLLMTiming.toFixed(2)}s
                                </div>
                                <div className="text-sm text-foreground">
                                    Average Response Time
                                </div>
                            </div>
                            <div className="text-center">
                                <div className="text-2xl font-bold text-primary">
                                    {data?.metrics.minLLMTiming.toFixed(2)}s
                                </div>
                                <div className="text-sm text-foreground">
                                    Fastest Response
                                </div>
                            </div>
                            <div className="text-center">
                                <div className="text-2xl font-bold text-primary">
                                    {data?.metrics.maxLLMTiming.toFixed(2)}s
                                </div>
                                <div className="text-sm text-foreground">
                                    Slowest Response
                                </div>
                            </div>
                        </div>
                    )}
                </CardContent>
            </Card>
        </div>
    );
};

export default AIUsageDashboard;
